import { Dtos, GroupedSection } from '#models';
import { Injectable, inject, computed } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PSSRStatus, UserRole } from 'src/enums/server';
import { ResponseValidator } from './response-validator.service';
import { ValidationErrors } from '@angular/forms';
import { Server } from '#enums';
import { StoreService } from './store';
import { AuthService } from './auth/auth.service';
import { MOCChecklistHelper } from './helpers/moc-checklist-helper.service';

/**
 * Service for validating PSSR status transitions, providing validation messages,
 * and enforcing business rules and permissions for PSSR operations.
 * Includes both PSSR-level and response-level permission checks.
 */
@Injectable({
  providedIn: 'root',
})
export class PssrValidatorService {
  private _translateService = inject(TranslateService);
  private _responseValidator = inject(ResponseValidator);
  private _storeService = inject(StoreService);
  private _authService = inject(AuthService);
  private _mocChecklistHelper = inject(MOCChecklistHelper);

  isValid = computed(() => {
    const bu = this._storeService.currentBu();
    if (!bu) return false;

    return !this.validationMessage();
  });

  /**
   * Get validation message for a PSSR based on its current status
   * Returns a computed signal that reactively updates based on store changes
   * @param responses Array of PSSR responses
   * @param standAloneActionItems Array of stand-alone action items
   * @returns Computed signal with translated validation message (string) or null if valid
   */
  validationMessage = computed(() => {
    const responses = this._storeService.currentPssrResponses();
    const standAloneActionItems = this._storeService.currentPssrStandAloneItems();
    const pssr = this._storeService.currentPssr();
    const bu = this._storeService.currentBu();

    if (!pssr || !bu) {
      return null;
    }

    const validationMessageKey = this.getValidationMessageKey(pssr, responses, standAloneActionItems, bu);

    return validationMessageKey !== null ? this._translateService.instant(validationMessageKey) : null;
  });

  /**
   * Get validation message key for a PSSR based on its current status
   * @param context The PSSR validation context
   * @param responses Array of PSSR responses
   * @param standAloneActionItems Array of stand-alone action items
   * @param bu Business unit configuration
   * @returns Validation message key (string) or null if valid
   */
  private getValidationMessageKey(
    context: Dtos.PSSR,
    responses: Dtos.PSSRResponse[],
    standAloneActionItems: Dtos.ResponseItem[],
    bu: Dtos.BusinessUnit
  ): string | null {
    let validationMessageKey = null;
    switch (context.Status) {
    case PSSRStatus.Draft:
      validationMessageKey = this.getValidationMessageWhenPsssrIsDraft(context, bu);
      break;

    case PSSRStatus.InProgress:
      validationMessageKey = this.getValidationMessageWhenPsssrIsInProgess(context, responses, bu);
      break;

    case PSSRStatus.Completed:
      validationMessageKey = this.getValidationMessageWhenPssrIsCompleted(
        context,
        responses,
        standAloneActionItems,
        bu
      );
      break;

    case PSSRStatus.PendingApproval:
      validationMessageKey = this.getValidationMessageWhenPssrIsPendingApproval(
        context,
        responses,
        standAloneActionItems
      );
      break;

    case PSSRStatus.Approved:
      validationMessageKey = this.getValidationMessageWhenPssrIsApproved(context, responses, standAloneActionItems);
      break;
    }

    return validationMessageKey;
  }

  /**
   * Get validation message for Draft status PSSR
   */
  private getValidationMessageWhenPsssrIsDraft(context: Dtos.PSSR, bu: Dtos.BusinessUnit): string | null {
    const noFinalApprover = !context.PSSRUsers.Users.some(u => u.Role === UserRole.Approver);

    if (!context.CurrentUser.CanEdit) {
      return 'pssrDetails.validation.onlyPSSRTeamLeadcanInitiatePSSR';
    }
    if (bu.RequireFinalApproverBeforeInitiation && noFinalApprover) {
      return 'pssrDetails.validation.requireFinalApproverBeforeInitiation';
    }
    return null;
  }

  /**
   * Get validation message for InProgress status PSSR
   */
  private getValidationMessageWhenPsssrIsInProgess(
    context: Dtos.PSSR,
    responses: Dtos.PSSRResponse[],
    bu: Dtos.BusinessUnit
  ): string | null {
    const noFinalApprover = !context.PSSRUsers.Users.some(u => u.Role === UserRole.Approver);

    const validateResponseResult = responses.reduce<null | ValidationErrors>((accum, response) => {
      const result = this._responseValidator.validate(response, bu);
      if (accum === null && result === null) {
        return null;
      }
      return Object.assign(accum || {}, result || {});
    }, null);

    if (validateResponseResult !== null) {
      return 'pssrDetails.validation.pssrMustAnswerAllQuestionsToCompleteWalkdown';
    }

    if (noFinalApprover) {
      return 'pssrDetails.validation.pssrMustHaveFinalApproverToCompleteWalkdown';
    }
    return null;
  }

  /**
   * Get validation message for Completed status PSSR
   */
  private getValidationMessageWhenPssrIsCompleted(
    context: Dtos.PSSR,
    responses: Dtos.PSSRResponse[],
    standAloneActionItems: Dtos.ResponseItem[],
    bu: Dtos.BusinessUnit
  ): string | null {
    if (bu && bu.AllowApproverToSubmitForApproval && context.CurrentUser.Role === UserRole.Approver) {
      return null;
    }
    if (!context.CurrentUser.CanEdit) {
      return 'pssrDetails.validation.onlyPSSRTeamLeadcanSubmitforPendingApproval';
    }
    if (this.hasP3Closed(responses, standAloneActionItems)) {
      return 'pssrDetails.validation.p3ActionItemsMustBeClosed';
    }
    return null;
  }

  hasP3Closed(responses: Dtos.PSSRResponse[], standAloneActionItems: Dtos.ResponseItem[]) {
    return this.hasActionItems(
      responses,
      standAloneActionItems,
      [Server.ResponseItemPriority.P3],
      [Server.ResponseItemStatus.Open, Server.ResponseItemStatus.Rejected]
    );
  }

  hasP4Closed(responses: Dtos.PSSRResponse[], standAloneActionItems: Dtos.ResponseItem[]) {
    return this.hasActionItems(
      responses,
      standAloneActionItems,
      [Server.ResponseItemPriority.P4],
      [Server.ResponseItemStatus.Open, Server.ResponseItemStatus.Rejected]
    );
  }

  /**
   * Get validation message for PendingApproval status PSSR
   */
  private getValidationMessageWhenPssrIsPendingApproval(
    context: Dtos.PSSR,
    responses: Dtos.PSSRResponse[],
    standAloneActionItems: Dtos.ResponseItem[]
  ): string | null {
    const hasRejectedP3Items = this.hasActionItems(
      responses,
      standAloneActionItems,
      [Server.ResponseItemPriority.P3],
      [Server.ResponseItemStatus.Rejected]
    );
    const p3Closed = this.hasP3Closed(responses, standAloneActionItems);

    if (hasRejectedP3Items && context.CurrentUser.Role === UserRole.Approver) {
      return null;
    }
    if (context.NextStatus === null) {
      return 'pssrDetails.validation.onlyFinalApprovercanApprovePSSR';
    }
    if (p3Closed) {
      return 'pssrDetails.validation.p3ActionItemsMustBeClosed';
    }

    return null;
  }

  private hasActionItems(
    responses: Dtos.PSSRResponse[],
    standAloneActionItems: Dtos.ResponseItem[],
    priorities: Server.ResponseItemPriority[] = [],
    statuses: Server.ResponseItemStatus[] = []
  ) {
    const items = [
      ...standAloneActionItems.filter(i => i.IsActionItem),
      ...responses.flatMap(r => r.ActionItems).filter(i => i.IsActionItem),
    ];

    return items.some(item => priorities.includes(item.Priority) && statuses.includes(item.Status));
  }

  /**
   * Get validation message for Approved status PSSR
   */
  private getValidationMessageWhenPssrIsApproved(
    context: Dtos.PSSR,
    responses: Dtos.PSSRResponse[],
    standAloneActionItems: Dtos.ResponseItem[]
  ): string | null {
    if (!context.CurrentUser.CanEdit && !context.CurrentUser.IsBusinessUnitAdmin) {
      return 'pssrDetails.validation.onlyPSSRTeamLeadClosePSSR';
    }
    if (this.hasP3Closed(responses, standAloneActionItems)) {
      return 'pssrDetails.validation.p3ActionItemsMustBeClosed';
    }
    if (this.hasP4Closed(responses, standAloneActionItems)) {
      return 'pssrDetails.validation.p4ActionItemsMustBeClosed';
    }
    return null;
  }

  // ==================== PSSR Permission Rules ====================

  /**
   * Determines if the current user can delete the PSSR
   * Rules:
   * - System admins can always delete
   * - Users can delete if PSSR status < Completed and they are:
   *   - Creator, OR Organization admin, OR Lead/Approver
   * - Business Unit admins can delete if status != Closed
   */
  canDeletePssr = computed(() => {
    const pssr = this._storeService.currentPssr();
    if (!pssr) return false;

    const currentUser = this._authService.getUserDetails();
    const isSysAdmin = currentUser?.isSysAdmin || false;

    const rule1 =
      pssr.Status < Server.PSSRStatus.Completed &&
      (currentUser?.email === pssr.CreatedBy ||
        pssr.CurrentUser?.IsOrgAdmin ||
        pssr.CurrentUser?.Role === Server.UserRole.Lead ||
        pssr.CurrentUser?.Role === Server.UserRole.Approver);
    const rule2 = pssr.Status !== Server.PSSRStatus.Closed && (pssr.CurrentUser?.IsBusinessUnitAdmin || false);

    return isSysAdmin || rule1 || rule2;
  });

  /**
   * Determines if the current user can reopen the PSSR
   * Rules:
   * - Business Unit admin can reopen if PSSR is Approved or Closed
   */
  canReopenPssr = computed(() => {
    const pssr = this._storeService.currentPssr();
    if (!pssr) return false;

    return (
      (pssr.CurrentUser?.IsBusinessUnitAdmin || false) &&
      (pssr.Status === Server.PSSRStatus.Approved || pssr.Status === Server.PSSRStatus.Closed)
    );
  });

  /**
   * Determines if the current PSSR status can be changed to next status
   * Rules:
   * - PSSR must not be deleted
   * - PSSR must have a valid next status
   */
  canChangePssrStatus = computed(() => {
    const pssr = this._storeService.currentPssr();
    if (!pssr || pssr.IsDeleted) return false;

    return pssr.NextStatusId > 0;
  });

  /**
   * Determines if the current user can reject the PSSR
   * Rules:
   * - PSSR status must be PendingApproval
   * - User must be an Approver
   * - PSSR must not be deleted
   */
  canRejectPssr = computed(() => {
    const pssr = this._storeService.currentPssr();
    if (!pssr || pssr.IsDeleted) return false;

    return pssr.Status === Server.PSSRStatus.PendingApproval && pssr.CurrentUser?.Role === Server.UserRole.Approver;
  });

  /**
   * Determines if the current PSSR can be sent to MOC Startup Approval
   * Rules:
   * - PSSR must be Approved or Closed
   * - User must be BU Admin or Lead
   * - PSSR must be imported from MOC (has ExternalId and is MOC application)
   * - PSSR must not be deleted
   */
  canSendToMOCStartupApproval = computed(() => {
    const pssr = this._storeService.currentPssr();
    if (!pssr || pssr.IsDeleted) return false;

    const isUserBUAdminOrLead =
      pssr.CurrentUser?.IsBusinessUnitAdmin ||
      pssr.CurrentUser?.Role === Server.UserRole.Lead ||
      pssr.CurrentUser?.Role === Server.UserRole.Approver;

    const isPssrImportedMOC = !!pssr.ExternalId?.length && pssr.ApplicationId === Server.ApplicationType.MOC;

    return (
      (pssr.Status === Server.PSSRStatus.Approved || pssr.Status === Server.PSSRStatus.Closed) &&
      isUserBUAdminOrLead &&
      isPssrImportedMOC
    );
  });

  /**
   * Determines if PSR can import MOC Action Items
   * Rules:
   * - PSSR status must be Initial or Initiated
   * - PSSR must have a MOC number
   * - PSSR must be manually created (no ApplicationId, no ExternalId)
   * - PSSR must not be deleted
   */
  canImportMOCAIs = computed(() => {
    const pssr = this._storeService.currentPssr();
    if (!pssr) return false;

    const hasNoApplicationId = !pssr.ApplicationId;
    const hasNoExternalId = !pssr.ExternalId || String(pssr.ExternalId).trim() === '';
    const isManuallyCreatedPssr = hasNoApplicationId && hasNoExternalId;
    const hasMOCNumber = !!pssr.MOC && String(pssr.MOC).trim().length > 0;

    return pssr.Status <= Server.PSSRStatus.Initiated && hasMOCNumber && isManuallyCreatedPssr && !pssr.IsDeleted;
  });

  // ==================== Response-Level Permission Rules ====================

  /**
   * Determines if the current user can answer or clear a Default response.
   * US 9420283: user cannot answer when:
   * - status not in [Initiated, InProgress]
   * - standalone response (SectionID==null) and status < Initiated
   * - !canAnswer AND !(AllowMembersToCompletePSSR && status in [InProgress,Completed] && userId in pssrUsers)
   */
  canRespond(response: Dtos.PSSRResponse, pssr: Dtos.PSSR, buObj: Dtos.BusinessUnit | null | undefined): boolean {
    // Status must be Initiated or InProgress
    if (![Server.PSSRStatus.Initiated, Server.PSSRStatus.InProgress].includes(pssr.Status)) {
      return false;
    }

    // Standalone response: status must have reached Initiated
    if (response.SectionID == null && pssr.Status < Server.PSSRStatus.Initiated) {
      return false;
    }

    // User needs canAnswer or members-complete feature
    if (!response.CanAnswer) {
      const canMembersComplete =
        buObj?.AllowMembersToCompletePSSR &&
        [Server.PSSRStatus.InProgress, Server.PSSRStatus.Completed].includes(pssr.Status) &&
        pssr.PSSRUsers.Users.some(u => u.UserID === pssr.CurrentUser.UserID);
      if (!canMembersComplete) {
        return false;
      }
    }

    return true;
  }

  /**
   * Determines if the current user can delete a response.
   * US 9420283: user cannot delete when:
   * - status not in [Initiated, Draft]
   * - AllowLeadsDeleteQuestions AND role != Lead AND !canEdit
   */
  canDeleteResponse(response: Dtos.PSSRResponse): boolean {
    const pssr = this._storeService.currentPssr();
    const buObj = this._storeService.currentBu();

    if (!pssr || !buObj) {
      return false;
    }

    if (![PSSRStatus.Initiated, PSSRStatus.Draft].includes(pssr.Status)) {
      return false;
    }

    if ((!response.SectionID || !response.ItemCode) && pssr.CurrentUser.CanEdit) {
      return true;
    }

    return !!buObj?.AllowLeadsDeleteQuestions && pssr.CurrentUser.Role === UserRole.Lead;
  }

  /**
   * Determines if the current user can add action items to a response.
   * US 9420283: handles standalone section and regular section logic.
   * @param response - the response to check
   * @param isSectionLead - whether the current user is a section lead (bypasses all restrictions)
   * @returns boolean indicating if action item can be added
   */
  canAddActionItem(response: Dtos.PSSRResponse, isSectionLead: boolean): boolean {
    // Check if MOC checklist is locked
    if (this.isMOCChecklistLockedForResponse(response)) {
      return false;
    }

    if (isSectionLead) {
      return true;
    }

    const pssr = this._storeService.currentPssr();

    if (!pssr) {
      return false;
    }

    if ([PSSRStatus.Draft, PSSRStatus.Approved, PSSRStatus.Closed].includes(pssr.Status)) {
      return false;
    }

    if (response.SectionID == null && pssr.Status < PSSRStatus.Initiated) {
      return false;
    }

    const allowMembersToCompletePSSR = this._storeService.allowMembersToCompletePSSR();
    return response.CanAnswer || pssr.CurrentUser.CanAddItem || !!allowMembersToCompletePSSR;
  }

  /**
   * Determines if a question can be added to the parent section.
   * Rules:
   * - User must be allowed to edit (status is Draft/Initiated/InProgress AND has CanEdit permission)
   * - Question section must be displayable (first response not MOC-locked)
   * - Parent section must not be a Signature section
   * @param parentSection - The parent section to check
   * @returns boolean indicating if a question can be added
   */
  canAddQuestion(parentSection: GroupedSection): boolean {
    const pssr = this._storeService.currentPssr();

    if (pssr === null) {
      return false;
    }

    const isUserAllowedToEdit =
      (pssr.Status === PSSRStatus.Draft ||
        pssr.Status === PSSRStatus.Initiated ||
        pssr.Status === PSSRStatus.InProgress) &&
      pssr.CurrentUser.CanEdit;

    const canDisplayQuestionSection = (): boolean => {
      if (parentSection.Responses?.length > 0) {
        const response = parentSection.Responses[0];
        if (this.isMOCChecklistLockedForResponse(response)) {
          return false;
        }
      }
      return true;
    };

    // Signatures section is locked at Initiated; all other edit statuses allow it
    if (parentSection.Type === Server.SectionType.Signature && pssr.Status === PSSRStatus.Initiated) {
      return false;
    }

    return isUserAllowedToEdit && canDisplayQuestionSection();
  }

  /**
   * Checks if a response's MOC checklist is locked.
   * @param response - The response to check
   * @returns true if the response has an ItemCode and its MOC checklist is locked, false otherwise
   */
  private isMOCChecklistLockedForResponse(response: Dtos.PSSRResponse | undefined): boolean {
    if (!response || !response.ItemCode) {
      return false;
    }
    return this._mocChecklistHelper.isMOCChecklistLocked(response.ItemCode);
  }

  // ==================== Comment Permission Rules ====================

  /**
   * Determines if the current user can add a comment to a response.
   * Rules:
   * - PSSR status must be <= Completed
   * - User must have CanAddItem permission, OR AllowMembersToCompletePSSR enabled, OR be section lead
   * @param isSectionLead - Whether the current user is a section lead
   * @returns boolean indicating if a comment can be added
   */
  canAddComment(response: Dtos.PSSRResponse, isSectionLead: boolean): boolean {
    return this.canAddActionItem(response, isSectionLead);
  }

  /**
   * Determines if the current user can edit a comment.
   * Rules:
   * - PSSR must not be deleted
   * - User must have CanEditItem permission OR AllowMembersToCompletePSSR enabled
   * - PSSR status must be < Approved
   * @param item - The comment item to check
   * @returns boolean indicating if the comment can be edited
   */
  canEditComment(item: Dtos.ResponseItem): boolean {
    const pssr = this._storeService.currentPssr();
    const allowMembersToCompletePSSR = this._storeService.allowMembersToCompletePSSR();

    if (pssr === null) {
      return false;
    }

    return (
      !pssr.IsDeleted && (item.CanEditItem || !!allowMembersToCompletePSSR) && pssr.Status < Server.PSSRStatus.Approved
    );
  }

  /**
   * Determines if the current user can delete a comment.
   * Rules:
   * - PSSR status must be InProgress
   * @returns boolean indicating if a comment can be deleted
   */
  canDeleteComment(): boolean {
    const pssr = this._storeService.currentPssr();

    if (pssr === null) {
      return false;
    }

    return pssr.Status === Server.PSSRStatus.InProgress;
  }
}
